import React, { useState } from "react";
import PopupModal from "../../../UI/Popup";
import { TJobData } from "../../../../store/candidate/types";
import {
  getJobCentreNameByVacancy,
  getJobTitleNameForCandidate,
  withDrawOrDeclineJob,
} from "../../../../services/candidateJobsService";
import { formatTimeframes } from "../../../Vacancy/timeframeApiHelper";
import { useFormik } from "formik";
import { createValidationSchema } from "../../../../utils/createValidationSchema";
import CustomButton from "../../../UI/Buttons/CustomButton";
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from "../../../../store";
import { isFulfilled } from "@reduxjs/toolkit";
import CharacterLimitTextarea from "../../../UI/Inputs/CharacterLimitTextarea";
import Warning from "../../../UI/Icons/Warning";

interface DeclineApplyJobModalProps {
  showApplyJobModal: boolean;
  setShowApplyJobModal: React.Dispatch<React.SetStateAction<boolean>>;
  onClickClose?: () => void;
  vacancyData: TJobData;
  handleApplyForJob: (vacancyData: TJobData) => void;
  getCandidateJobList?: () => void;
  setShowAcceptedJobs?: (show: boolean) => void;
  onRefresh?: () => void;
  showSingleDayView?: (show: boolean) => void;
  setShowViewModal?: (show: boolean) => void;
}

const DeclineApplyJobModal: React.FC<DeclineApplyJobModalProps> = ({
  showApplyJobModal,
  handleApplyForJob,
  onClickClose,
  setShowApplyJobModal,
  vacancyData,
  getCandidateJobList,
  setShowAcceptedJobs,
  onRefresh,
  showSingleDayView,
  setShowViewModal
}) => {
  const dispatch = useDispatch();

  const profileData = useSelector(
    (state) => state.onboarding.userpersonaldetails
  );

  const handleClose = () => {
    setShowApplyJobModal(false);
    resetForm && resetForm();
    if (onClickClose) {
      onClickClose();
    }
  };

  const [showDeclinedDonePopup, setShowDeclinedDonePopup] = useState(false);
  
  const handleDeclinedDoneClose = () => {
    setShowDeclinedDonePopup(false)
    getCandidateJobList && getCandidateJobList();
    // Reset form fields after successful POST
    resetForm && resetForm();
    onRefresh && onRefresh();
  }

  const { handleSubmit, values, errors, setFieldValue, resetForm } =
    useFormik<any>({
      validationSchema: createValidationSchema("withdraw_job_application"),
      initialValues: {
        comments: "",
      },
      onSubmit: async (values: any) => {
        const data = {
          uuid: vacancyData?.uuid,
          candidate_uuid: profileData?.uuid ?? "",
          action: "decline",
          comments: values.comments || "",
          type: ""
        };
        const applyJobResponse = await withDrawOrDeclineJob(dispatch, data);
        if (isFulfilled(applyJobResponse)) {
          setShowApplyJobModal(false);
          setShowAcceptedJobs && setShowAcceptedJobs(false);
          setShowViewModal?.(false); // Closing view centre profile modal
          
          setShowDeclinedDonePopup(true)          
        }
      },
    });

  const handleChange = (data: string) => {
    setFieldValue("comments", data);
  };

  return (
    <>
      <PopupModal
        visible={showDeclinedDonePopup}
        showCloseBtn={true}
        onClickClose={handleDeclinedDoneClose}
        customWidth={650}
      >
        <div className="gap-20 fs-22">
          You have declined your accepted job
        </div>
        <div className="gap-20 fs-13">
          {getJobTitleNameForCandidate(vacancyData)} {" "}
          at {getJobCentreNameByVacancy(vacancyData)},{" "}
          {vacancyData?.centre?.address} on {" "}
          {vacancyData?.timeframes &&
            formatTimeframes(vacancyData?.timeframes)?.map(
              (formatedTimeData, index) => (
                <div
                  key={index}
                  className={`fs-13 d-inline ${
                    formatedTimeData.alert ? "text-alert fw-700" : "fw-400"
                  }`}
                >
                  {formatedTimeData.label}
                </div>
              )
            )}
        </div>
        <div className="gap-20 fs-13">
          You will not see this job listing now.
        </div>
      </PopupModal>

      <PopupModal
        visible={showApplyJobModal}
        showCloseBtn={true}
        onClickClose={handleClose}
        customWidth={650}
      >
        <div className="gap-20 fs-22">
          Are you sure you want to decline this accepted job?
        </div>
        <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6">
          <Warning />
          Declining accepted jobs or withdrawing applications are noted on your
          record. A consultant may be in contact with you if you decline or
          withdraw too many times. You will not see this job listing after you
          decline.
        </div>
        <div className="d-flex flex-column gap-8">
          <div className="fs-13">
            You are about to decline the following accepted job:{" "}
            {getJobTitleNameForCandidate(vacancyData)}
            at {getJobCentreNameByVacancy(vacancyData)},{" "}
            {vacancyData?.centre?.address} on {" "}
            {vacancyData?.timeframes &&
              formatTimeframes(vacancyData?.timeframes)?.map(
                (formatedTimeData, index) => (
                  <div
                    key={index}
                    className={`fs-13 d-inline ${
                      formatedTimeData.alert ? "text-alert fw-700" : "fw-400"
                    }`}
                  >
                    {formatedTimeData.label}
                  </div>
                )
              )}
          </div>
          <div className="fs-13">Please include a reason for declining.</div>
        </div>

        <CharacterLimitTextarea
          maxLength={1000}
          value={values.comments}
          onChange={(value) => handleChange(value)}
          error={
            typeof errors.comments === "string" ? errors.comments : undefined
          }
          type="decline"
        />

        <div className="d-flex justify-content-end">
          <CustomButton
            className="btn-theme bg-alert text-white"
            onClick={handleSubmit}
            variant="primary"
            contentClass="d-flex"
          >
            Confirm and Decline
          </CustomButton>
        </div>

      </PopupModal>
    </>
  );
};

export default DeclineApplyJobModal;
